<template>
  <div class="chat-interface">
    <ChatSidebar
      :agents="agents"
      :current-agent="currentAgent"
      @select-agent="selectAgent"
    />
    <div class="main-content">
      <div class="chat-header">
        <h2>{{ currentAgentName }}</h2>
        <button @click="clearMessages" class="clear-button">
          Effacer la discussion
        </button>
      </div>
      <ChatMessages
        :messages="messages"
        :agents="agents"
        :current-agent="currentAgent"
        :isLoading="isLoading"
      />
      <ChatInput
        :current-agent="currentAgent"
        @send-message="sendMessage"
        @file-upload="handleFileUpload"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { marked } from "marked";
import DOMPurify from "dompurify";
import ChatSidebar from "./ChatSidebar.vue";
import ChatMessages from "./ChatMessages.vue";
import ChatInput from "./ChatInput.vue";

const store = useStore();
const currentAgent = ref("thomas");
const agents = [
  { id: "thomas", name: "Thomas", avatar: require("@/assets/thomas.png") },
  // { id: "price", name: "AI Price Checker", avatar: require("@/assets/thomas.png") },
  // { id: "helper", name: "AI Service Helper", avatar: require("@/assets/thomas.png") },
];

const currentAgentName = computed(() => {
  return agents.find((agent) => agent.id === currentAgent.value)?.name || "";
});

// Get messages and threadId from the Vuex chat module.
// Convert and sanitize messages
const messages = computed(() => {
  return store.getters["chat/allMessages"].map((message) => {
    if (message.role === "assistant") {
      // Convert markdown to HTML and sanitize
      const rawHtml = marked(message.content);
      const cleanHtml = DOMPurify.sanitize(rawHtml);
      return { ...message, content: cleanHtml };
    }
    return message;
  });
});
const isLoading = computed(() => store.getters["chat/loading"]);

// Optionally, define welcome messages for each agent.
const welcomeMessages = {
  thomas:
    "Bonjour et bienvenue sur notre service d'assistance technique. Pour mieux diagnostiquer le problème de votre appareil, merci de nous indiquer :\
    <br>• La marque et le modèle de votre appareil\
    <br>• Les dommages visibles ou les dysfonctionnements constatés\
    <br>• Les actions déjà entreprises pour résoudre le problème\
    <br>• Nous ne pouvons pas établir de diagnostic sans ces informations de votre part\
    <br><br>\
    Ces informations nous aideront à vous fournir une aide précise et adaptée.",
  // price: "Hi! I can provide repair price estimates. Please describe your device and the issue.",
  // helper: "Welcome! I'm here to help with any service-related questions you may have.",
};

// New function to add welcome message
const addWelcomeMessage = () => {
  if (messages.value.length === 0) {
    store.commit("chat/ADD_MESSAGE", {
      role: "assistant",
      content:
        welcomeMessages[currentAgent.value] ||
        "Bonjour et bienvenue sur notre service d'assistance technique. Pour mieux diagnostiquer le problème de votre appareil, merci de nous indiquer :\
        <br>• La marque et le modèle de votre appareil\
        <br>• Les dommages visibles ou les dysfonctionnements constatés\
        <br>• Les actions déjà entreprises pour résoudre le problème\
        <br><br>\
        Ces informations nous aideront à vous fournir une aide précise et adaptée.",
    });
  }
};
// Clear messages with confirmation and re-add welcome message
async function clearMessages() {
  if (confirm("Êtes-vous sûr de vouloir effacer tous les messages ?")) {
    try {
      // Dispatch the new Vuex action instead of committing directly
      await store.dispatch("chat/clearMessages");
      addWelcomeMessage();
    } catch (error) {
      console.error("Error clearing messages:", error);
    }
  }
}

// Update agent selection to include welcome message
async function selectAgent(agentId) {
  currentAgent.value = agentId;
  try {
    await store.dispatch("chat/clearMessages");
    addWelcomeMessage();
  } catch (error) {
    console.error("Erreur lors du changement d'agent:", error);
  }
}

// Dispatch the store action for sending a message.
function sendMessage(messageText) {
  if (!messageText.trim()) return;
  store.dispatch("chat/sendMessage", messageText);
}

watch(isLoading, (newValue) => {
  if (!newValue) {
    // When loading is complete, scroll to the bottom of the chat
    scrollToBottom();
  }
});

// Function to scroll to the bottom of the chat
function scrollToBottom() {
  // You might need to adjust this depending on your DOM structure
  const chatContainer = document.querySelector(".chat-messages");
  if (chatContainer) {
    chatContainer.scrollTop = chatContainer.scrollHeight;
  }
}

// File upload handling: commit the new file message directly.
function handleFileUpload(file) {
  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      store.commit("chat/ADD_MESSAGE", {
        role: "user",
        content: "J'ai téléchargé une image:",
        image: e.target.result,
      });
    };
    reader.readAsDataURL(file);
  }
}

onMounted(() => {
  addWelcomeMessage();
});
</script>

<style scoped>
.chat-interface {
  display: flex;
  flex-direction: row;
  width: 100%;
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.chat-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.clear-button {
  padding: 0.5rem 1rem;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.clear-button:hover {
  background: #c0392b;
}

@media (max-width: 768px) {
  .chat-interface {
    flex-direction: column; /* Stack sidebar and main content vertically */
  }
  .sidebar {
    width: 100%; /* Make sidebar full width on mobile */
    border-right: none; /* Remove right border when stacked */
    border-bottom: 1px solid #e5e7eb; /* Add bottom border to separate from content */
  }
  .main-content {
    width: 100%; /* Ensure main content takes full width */
  }
}
</style>
